import { z } from 'zod'
import { instantSchema } from '../validators/temporal.validator.js'
import { vendorServiceSchema } from '../enums/vendor-service.enum.js'
import { browserAssetUrlSchema } from '../helpers/asset-ref.js'
import { invitedContactDtoSchema } from './user.dto.js'
import { nullableCalendarDaySchema } from '../validators/calendar-day.validator.js'

/**
 * A vendor — a supplier that operates across tenants, managed by its own VENDOR
 * users (invited by a platform SUPER_ADMIN). `userCount` summarizes its users
 * for the super-admin list.
 */
export const vendorDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Business mailing address; each part is null until the vendor fills it in. */
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  /** The vendor's cover photograph, signed for the browser; null if none yet. */
  coverImageUrl: browserAssetUrlSchema.nullable(),
  /** The services the vendor offers (self-configured); gates its config + venue approval. */
  services: z.array(vendorServiceSchema),
  /** Coordinates geocoded from the business address; null until located. Drives
   * "venues near me" matching. */
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  /** How far the vendor will travel from its base, in miles; null if unset. */
  serviceRadiusMiles: z.number().int().nonnegative().nullable(),
  createdAt: instantSchema,
  userCount: z.number().int().nonnegative(),
  /**
   * When the company was deactivated, or null while it works. A company that has
   * been on an event is never deleted, only this (docs/planner-vendors.md, 39).
   * Defaulted while an older API is still answering.
   */
  deactivatedAt: instantSchema.nullable().default(null),
  /**
   * Whether an owner has kept the company out of venue search. Venues it already
   * works with find it either way (issue #73). Defaulted while an older API is
   * still answering.
   */
  hiddenFromSearch: z.boolean().default(false),
  /**
   * The person the company's quote requests are emailed to; null for its
   * owners. Defaulted while an older API is still answering.
   */
  quoteRecipientId: z.string().uuid().nullable().default(null),
  /**
   * A company someone signed up on their own, until they confirm their email: it
   * can't ask to serve a venue and isn't in venues' search
   * (docs/public-site.spec.md, "Vendor signup"). Defaulted while an older API is
   * still answering.
   */
  awaitingEmailConfirmation: z.boolean().default(false),
})

export type VendorDto = z.infer<typeof vendorDtoSchema>

export const vendorListDtoSchema = z.object({
  vendors: z.array(vendorDtoSchema),
  total: z.number().int().nonnegative(),
})

export type VendorListDto = z.infer<typeof vendorListDtoSchema>

/**
 * A venue as seen through the vendor lens — the super admin's association picker
 * and the vendor's read-only list. Deliberately light (no spaces/inventory);
 * `tenantName` shows which organization owns it, since vendors span tenants.
 */
export const vendorVenueDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  address: z.string().nullable(),
  tenantName: z.string(),
  /** Whether this venue is currently linked to the vendor in question. */
  linked: z.boolean(),
})

export type VendorVenueDto = z.infer<typeof vendorVenueDtoSchema>

export const vendorVenueListDtoSchema = z.object({
  venues: z.array(vendorVenueDtoSchema),
  total: z.number().int().nonnegative(),
})

export type VendorVenueListDto = z.infer<typeof vendorVenueListDtoSchema>

/** A venue a vendor is actively linked to, as the vendor sees it — with the
 * services the venue has approved for this vendor there. */
export const vendorLinkedVenueDtoSchema = z.object({
  venueId: z.string().uuid(),
  name: z.string(),
  address: z.string().nullable(),
  tenantName: z.string(),
  approvedServices: z.array(vendorServiceSchema),
})

export type VendorLinkedVenueDto = z.infer<typeof vendorLinkedVenueDtoSchema>

export const vendorLinkedVenueListDtoSchema = z.object({
  venues: z.array(vendorLinkedVenueDtoSchema),
  total: z.number().int().nonnegative(),
})

export type VendorLinkedVenueListDto = z.infer<typeof vendorLinkedVenueListDtoSchema>

/**
 * A vendor associated with a venue, as the venue's admin sees it when approving
 * services: what the vendor `offered` and which of those the venue has `approved`.
 * `approved` is always a subset of `offered`.
 */
export const vendorServiceApprovalDtoSchema = z.object({
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  offered: z.array(vendorServiceSchema),
  approved: z.array(vendorServiceSchema),
  /** How many upcoming events at this venue rent the vendor's furniture. */
  upcomingRentalEvents: z.number().int().nonnegative().default(0),
  /**
   * Who the venue deals with there. Contact details come with the company being
   * the venue's vendor, never with finding it in search. Defaulted while an older
   * API is still answering.
   */
  contact: invitedContactDtoSchema.nullable().default(null),
  /**
   * Whether this venue set the company up, and so may correct its owner's email
   * while the owner has not accepted (decision 27).
   */
  setUpHere: z.boolean().default(false),
  /** This venue recommends the company (docs/vendor-growth.spec.md, "Reputation"). */
  recommended: z.boolean().default(false),
})

export type VendorServiceApprovalDto = z.infer<typeof vendorServiceApprovalDtoSchema>

export const vendorServiceApprovalListDtoSchema = z.object({
  vendors: z.array(vendorServiceApprovalDtoSchema),
  total: z.number().int().nonnegative(),
})

export type VendorServiceApprovalListDto = z.infer<typeof vendorServiceApprovalListDtoSchema>

/** An upcoming event a company comes off when a venue stops working with it, or it is deactivated. */
export const vendorRemovalEventDtoSchema = z.object({
  eventId: z.string().uuid(),
  eventName: z.string(),
  eventDate: nullableCalendarDaySchema,
  venueName: z.string(),
})

/** One of the company's rented pieces on an upcoming event, and the plans it is on. */
export const vendorRemovalPieceDtoSchema = z.object({
  itemName: z.string(),
  quantity: z.number().int().nonnegative(),
  /**
   * Each plan that has it placed. An open plan keeps its pieces marked as
   * removed until someone takes them off; a settled one (confirmed, or over by
   * its date) keeps them as it was.
   */
  plans: z.array(z.object({ phaseLabel: z.string(), pieces: z.number().int().positive(), settled: z.boolean() })),
})

/** An upcoming event that rents the company's furniture, and the pieces that come off it. */
export const vendorRemovalRentalDtoSchema = vendorRemovalEventDtoSchema.extend({
  pieces: z.array(vendorRemovalPieceDtoSchema),
})

/**
 * What stopping work with a company would do, shown before it is done
 * (docs/planner-vendors.md, decisions 36 and 39). Past and canceled events keep
 * it on record, so they are not listed.
 */
export const vendorRemovalPreviewDtoSchema = z.object({
  vendorName: z.string(),
  /** The upcoming events it comes off, soonest first; undated ones last. */
  events: z.array(vendorRemovalEventDtoSchema),
  /** How many upcoming events rent its furniture: `rentals.length`. */
  rentalEvents: z.number().int().nonnegative(),
  /**
   * The upcoming events renting its furniture, in the same order, with each
   * piece that comes off and the plans it is on. Defaulted while an older API
   * is still answering.
   */
  rentals: z.array(vendorRemovalRentalDtoSchema).default([]),
  /**
   * Who will be told: the company's contact, once, listing the events; each
   * couple whose planner it was or whose plans had its furniture; and the
   * planners of those plans. Never the person asking. Defaulted while an older
   * API is still answering.
   */
  told: z.array(z.object({ name: z.string(), as: z.enum(['company', 'couple', 'planner']) })).default([]),
})

export type VendorRemovalPreviewDto = z.infer<typeof vendorRemovalPreviewDtoSchema>

/**
 * A vendor's relationship to a venue it might serve: not requested, a pending
 * request, or an active association. The discovery list and the mapper that
 * writes it share this.
 */
const NEARBY_VENUE_STATUSES = ['none', 'pending', 'active'] as const
export const nearbyVenueStatusSchema = z.enum(NEARBY_VENUE_STATUSES)
export type NearbyVenueStatus = z.infer<typeof nearbyVenueStatusSchema>

/** A venue near a vendor, for the vendor's "find venues" discovery. `status` is
 * this vendor's relationship to it: not requested, a pending request, or an
 * active association. */
export const nearbyVenueDtoSchema = z.object({
  venueId: z.string().uuid(),
  name: z.string(),
  address: z.string().nullable(),
  distanceMiles: z.number().nonnegative(),
  status: nearbyVenueStatusSchema,
})

export const nearbyVenueListDtoSchema = z.object({
  venues: z.array(nearbyVenueDtoSchema),
  total: z.number().int().nonnegative(),
})

export type NearbyVenueListDto = z.infer<typeof nearbyVenueListDtoSchema>

/** A vendor's pending request to serve a venue, as the venue's admin sees it. */
export const vendorRequestDtoSchema = z.object({
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  services: z.array(vendorServiceSchema),
})

export const vendorRequestListDtoSchema = z.object({
  requests: z.array(vendorRequestDtoSchema),
  total: z.number().int().nonnegative(),
})

export type VendorRequestListDto = z.infer<typeof vendorRequestListDtoSchema>

/**
 * A company as a venue manager finds it when adding a vendor
 * (docs/planner-vendors.md, "Finding a vendor").
 *
 * What a marketplace listing would show, and no more: the company and what it
 * offers. Never its people or their addresses, and never the other venues or
 * organizations it works with — whom a company works for is each venue's own
 * business. Contact details come with adding it to the venue.
 */
export const vendorSearchResultDtoSchema = z.object({
  vendorId: z.string().uuid(),
  name: z.string(),
  services: z.array(vendorServiceSchema),
  /** Past events at this venue, not canceled, that the company was on. */
  eventsHere: z.number().int().nonnegative(),
  /** Already one of this venue's vendors. */
  linked: z.boolean(),
})

export type VendorSearchResultDto = z.infer<typeof vendorSearchResultDtoSchema>

export const vendorSearchResultListDtoSchema = z.object({
  /** The first matches by name; a search that matches more lists only these. */
  vendors: z.array(vendorSearchResultDtoSchema),
  /** How many companies match in all, which can be more than are listed. */
  total: z.number().int().nonnegative(),
})

export type VendorSearchResultListDto = z.infer<typeof vendorSearchResultListDtoSchema>

/**
 * One of a venue's vendors, as the planner of an event there picks from them
 * (docs/vendor-growth.spec.md): what it offers, and nothing of its history with
 * the venue, which is the venue's own.
 */
export const venueVendorChoiceDtoSchema = z.object({
  vendorId: z.string().uuid(),
  name: z.string(),
  services: z.array(vendorServiceSchema),
})

export type VenueVendorChoiceDto = z.infer<typeof venueVendorChoiceDtoSchema>

export const venueVendorChoiceListDtoSchema = z.object({
  /** The first matches by name; a search that matches more lists only these. */
  vendors: z.array(venueVendorChoiceDtoSchema),
  /** How many of the venue's vendors match in all. */
  total: z.number().int().nonnegative(),
})

export type VenueVendorChoiceListDto = z.infer<typeof venueVendorChoiceListDtoSchema>
